import React, { useState, useEffect } from 'react';
import api, { getApiErrorMessage } from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Plus, Trash2, ChevronRight, X, FileText, Check, Upload, ArrowLeft, Wand2 } from 'lucide-react';

import { TemplatePicker, SupplierSelect, ContractManagerSelect, ApprovalRouteBuilder, ContractDetailsStep, ContractWritingStep } from './ContractHelpers';
import { EMPTY_FORM } from '../contractConstants';

export function NewContractWizard({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);           // 1=template, 2=details, 3=write
  const [selectedTemplate, setSelectedTemplate] = useState(null); // null = blank
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) { setStep(1); setSelectedTemplate(null); setForm({ ...EMPTY_FORM }); setBody(''); }
  }, [open]);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setStep(2);
  };

  const handleBlank = () => {
    setSelectedTemplate(null);
    setStep(2);
  };

  const handleToWriting = async () => {
    if (!form.contract_number || !form.description) {
      toast.error('Contract number and description are required');
      return;
    }
    // Apply smart fields to template content
    setApplying(true);
    try {
      let resolvedBody = selectedTemplate?.content || '';
      if (resolvedBody) {
        // Build overrides from form data
        const overrides = {
          PAYMENT_TERMS:    form.payment_terms || 'Net 30',
          GOVERNING_LAW:    form.governing_law || '[Governing Law]',
          NOTICE_PERIOD:    `${form.notice_period} days`,
          CONTRACT_VALUE:   form.contract_value ? `${parseFloat(form.contract_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '[Contract Value]',
          PARTY_B_NAME:     form.supplier_name || '[Supplier Name]',
          EFFECTIVE_DATE:   form.contract_start || '[Effective Date]',
          EXPIRY_DATE:      form.contract_end || '[Expiry Date]',
          EXECUTION_DATE:   form.contract_start || '[Execution Date]',
          TERM_LENGTH:      form.term_length || '[Term Length]',
          LIABILITY_CAP:    form.liability_cap || '100% of fees',
        };
        const res = await api.post('/api/contracts/resolve-smart-fields', {
          content: resolvedBody,
          overrides,
        });
        resolvedBody = res.data.content;
      }
      setBody(resolvedBody);
      setStep(3);
    } catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Failed to apply smart fields'); }
    setApplying(false);
  };

  const saveContract = async () => {
    setSaving(true);
    try {
      // Create contract record
      await api.post('/api/data/supplier-contracts', { ...form, workflow_status: 'DRAFT' });
      // Save body
      if (body.trim()) {
        await api.put(`/api/contracts/${form.contract_number}/body`, { body });
      }
      toast.success('Contract created and saved!');
      onCreated();
      onClose();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const stepTitles = [
    { n: 1, label: 'Choose Template' },
    { n: 2, label: 'Contract Details' },
    { n: 3, label: 'Write Contract' },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto p-0">
        {/* Step indicator */}
        <div className="px-6 pt-5 pb-3 border-b">
          <div className="flex items-center gap-0">
            {stepTitles.map((s, i) => (
              <React.Fragment key={s.n}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    step === s.n ? 'bg-primary text-white' :
                    step > s.n  ? 'bg-green-500 text-white' :
                    'bg-muted text-muted-foreground'
                  }`}>{step > s.n ? '✓' : s.n}</div>
                  <span className={`text-sm font-medium ${
                    step === s.n ? 'text-primary' : step > s.n ? 'text-green-600' : 'text-muted-foreground'
                  }`}>{s.label}</span>
                </div>
                {i < stepTitles.length - 1 && (
                  <div className={`flex-1 h-px mx-3 ${ step > s.n ? 'bg-green-400' : 'bg-border' }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 py-5">
          {step === 1 && <TemplatePicker onSelect={handleTemplateSelect} onBlank={handleBlank} />}
          {step === 2 && <ContractDetailsStep form={form} onChange={setForm} template={selectedTemplate} />}
          {step === 3 && <ContractWritingStep body={body} onChange={setBody} isReadOnly={false} />}
        </div>

        {/* Footer buttons */}
        <div className="px-6 py-4 border-t flex items-center justify-between bg-card/95 backdrop-blur sticky bottom-0">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            {step === 2 && (
              <Button onClick={handleToWriting} disabled={applying || !form.contract_number || !form.description}>
                <Wand2 className="h-4 w-4 mr-1" />
                {applying ? 'Applying Smart Fields...' : 'Continue to Writing'}
              </Button>
            )}
            {step === 3 && (
              <Button onClick={saveContract} disabled={saving}>
                {saving ? 'Saving...' : 'Save Contract'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
